/* LBL header search: typewriter placeholder, rotating header message and predictive results.
   Uses Shopify's predictive search API (/search/suggest.json). No dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Rotating message next to the logo
  const items = document.querySelectorAll('.lbl-header-message__item');
  if (items.length > 1 && !reduceMotion) {
    let i = 0;
    setInterval(() => {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, 5000);
  }

  if (customElements.get('lbl-header-search')) return;

  class LblHeaderSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.lbl-header-search__results');
      this.phrases = (this.dataset.phrases || '').split('|').map((p) => p.trim()).filter(Boolean);
      this.active = -1;
      this.timer = null;
      this.controller = null;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.input.addEventListener('focus', () => { this.stopTyping(); if (this.input.value.trim()) this.open(); });
      this.input.addEventListener('blur', () => { if (!this.input.value) this.startTyping(); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });

      this.startTyping();
    }

    /* ---------- Typewriter placeholder ---------- */
    startTyping() {
      if (reduceMotion || this.phrases.length < 2 || this.typing) return;
      this.typing = true;
      let p = 0, c = 0, deleting = false;
      const step = () => {
        if (!this.typing) return;
        const phrase = this.phrases[p];
        c += deleting ? -1 : 1;
        this.input.setAttribute('placeholder', phrase.slice(0, Math.max(c, 0)));
        let delay = deleting ? 35 : 70;
        if (!deleting && c >= phrase.length) { deleting = true; delay = 1800; }
        else if (deleting && c <= 0) { deleting = false; p = (p + 1) % this.phrases.length; delay = 350; }
        this.typeTimer = setTimeout(step, delay);
      };
      step();
    }

    stopTyping() {
      this.typing = false;
      clearTimeout(this.typeTimer);
      this.input.setAttribute('placeholder', this.phrases[0] || this.input.getAttribute('placeholder'));
    }

    /* ---------- Predictive results ---------- */
    onInput() {
      clearTimeout(this.timer);
      const q = this.input.value.trim();
      if (!q) { this.close(); return; }
      this.timer = setTimeout(() => this.fetchResults(q), 220);
    }

    async fetchResults(q) {
      this.controller?.abort();
      this.controller = new AbortController();
      const base = this.input.dataset.suggestUrl || '/search/suggest';
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,page',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const res = await fetch(`${base}.json?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const data = await res.json();
        this.render(q, data.resources?.results || {});
      } catch (e) {
        if (e.name !== 'AbortError') this.render(q, {});
      }
    }

    render(q, r) {
      const products = r.products || [], collections = r.collections || [], pages = r.pages || [];
      const money = (v) => (v === undefined || v === null || v === '' ? '' : `$${Number(v).toFixed(2)}`);
      let html = '';
      if (products.length) {
        html += '<p class="lbl-sr__heading">Products</p><ul class="lbl-sr__products" role="group">';
        html += products.map((p) => `
          <li><a class="lbl-sr__product" role="option" href="${escapeHtml(p.url)}">
            <span class="lbl-sr__thumb">${p.image ? `<img src="${escapeHtml(this.thumb(p.image))}" alt="" loading="lazy" width="60" height="60">` : ''}</span>
            <span class="lbl-sr__text"><span class="lbl-sr__title">${escapeHtml(p.title)}</span>
            <span class="lbl-sr__price">${p.price_max && p.price_max !== p.price_min ? 'From ' : ''}${money(p.price_min ?? p.price)}</span></span>
          </a></li>`).join('');
        html += '</ul>';
      }
      const links = [...collections.map((c) => ({ t: c.title, u: c.url, k: 'Collection' })), ...pages.map((p) => ({ t: p.title, u: p.url, k: 'Page' }))];
      if (links.length) {
        html += '<p class="lbl-sr__heading">Collections &amp; pages</p><ul class="lbl-sr__links" role="group">';
        html += links.slice(0, 6).map((l) => `<li><a role="option" href="${escapeHtml(l.u)}"><span>${escapeHtml(l.t)}</span><small>${l.k}</small></a></li>`).join('');
        html += '</ul>';
      }
      if (!html) html = `<p class="lbl-sr__empty">No results for “${escapeHtml(q)}”. Try a set number, theme or product type.</p>`;
      html += `<a class="lbl-sr__all" role="option" href="${this.querySelector('form').action}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last">View all results for “${escapeHtml(q)}” →</a>`;
      this.results.innerHTML = html;
      this.active = -1;
      this.open();
    }

    thumb(src) {
      try { const u = new URL(src, location.origin); u.searchParams.set('width', '120'); return u.toString(); } catch { return src; }
    }

    open() { if (!this.results.innerHTML) return; this.results.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
    close() { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.active = -1; }

    onKeydown(e) {
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (e.key === 'Escape') { this.close(); return; }
      if (this.results.hidden || !options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      if (e.key === 'Enter') {
        if (this.active >= 0) { e.preventDefault(); options[this.active].click(); }
        return;
      }
      e.preventDefault();
      this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((o, i) => o.classList.toggle('is-active', i === this.active));
      options[this.active].scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', '');
    }
  }

  customElements.define('lbl-header-search', LblHeaderSearch);
})();

// Account popup: Shopify pins it 20px from the window's top-right (over the icon); drop it just under the icon instead
document.addEventListener('click', (e) => {
  const host = e.target.closest && e.target.closest('shopify-account');
  if (!host) return;
  const r = host.getBoundingClientRect();
  host.style.setProperty('--lbl-acct-top', `${Math.round(r.bottom + 10)}px`);
  host.style.setProperty('--lbl-acct-right', `${Math.max(12, Math.round(document.documentElement.clientWidth - r.right - 8))}px`);
}, true);
